import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import React from "react";
import VirtualListbox from "./VirtualListbox";

/** Option wrapper for async autocomplete */
export interface AsynAutocompleteOption<V = any> {
  value: V;
  key: string | number;
}

/* ----------------------------- Typed Props ----------------------------- */

interface BaseProps<T extends AsynAutocompleteOption<V>, V> {
  options: T[];
  isLoading: boolean;
  onInputChange: (inputValue: string) => void;
  label?: string;
  placeholder?: string;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  renderOption?: (props: object, option: T) => React.ReactNode;
  getOptionLabel?: (option: T) => React.ReactNode;
  infinite?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
}

interface SingleSelectProps<T extends AsynAutocompleteOption<V>, V>
  extends BaseProps<T, V> {
  multiple?: false;
  value?: T | null;
  onChange?: (value: T | null) => void;
}

interface MultiSelectProps<T extends AsynAutocompleteOption<V>, V>
  extends BaseProps<T, V> {
  multiple: true;
  value?: T[] | null;
  onChange?: (value: T[] | null) => void;
  /** Maximum number of selectable items */
  maxSelected?: number;
}

/** Unified prop type */
export type AsyncAutocompleteProps<
  T extends AsynAutocompleteOption<V>,
  V = any
> = SingleSelectProps<T, V> | MultiSelectProps<T, V>;

/* ----------------------------- Component ----------------------------- */

export default function AsyncAutocomplete<
  T extends AsynAutocompleteOption<V>,
  V = any
>(props: AsyncAutocompleteProps<T, V>) {
  const {
    options,
    isLoading,
    onInputChange,
    label,
    placeholder,
    infinite,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    width,
    minWidth,
    maxWidth,
    renderOption,
    getOptionLabel,
  } = props;

  const multiple = props.multiple ?? false;
  const maxSelected = multiple
    ? (props as MultiSelectProps<T, V>).maxSelected ?? Infinity
    : 1;

  // Internal selected value state
  const [selectedValue, setSelectedValue] = React.useState<T | T[] | null>(
    props.value ?? (multiple ? [] : null)
  );

  // Filter out selected options
  const filteredOptions = React.useMemo(() => {
    if (!multiple) {
      const selected = selectedValue as T | null;
      return selected ? options.filter((o) => o.key !== selected.key) : options;
    }
    const selectedKeys = new Set((selectedValue as T[]).map((s) => s.key));
    return options.filter((o) => !selectedKeys.has(o.key));
  }, [options, selectedValue, multiple]);

  // Merge logic for single vs multiple
  const mergedOptions = React.useMemo(() => {
    if (!selectedValue) return filteredOptions;
    if (!multiple) {
      const selected = selectedValue as T;
      const exists = filteredOptions.some((o) => o.key === selected.key);
      return exists ? filteredOptions : [selected, ...filteredOptions];
    }
    return filteredOptions;
  }, [filteredOptions, selectedValue, multiple]);

  const limitReached =
    multiple && Array.isArray(selectedValue)
      ? selectedValue.length >= maxSelected
      : false;

  return (
    <Autocomplete
      multiple={multiple}
      value={selectedValue}
      onChange={(_event, newValue) => {
        if (multiple) {
          const newArray = newValue as T[];
          if (newArray.length > maxSelected) return;
          setSelectedValue(newArray);
          (props.onChange as (v: T[] | null) => void)?.(newArray);
        } else {
          setSelectedValue(newValue as T | null);
          (props.onChange as (v: T | null) => void)?.(newValue as T | null);
        }
      }}
      filterOptions={(x) => x}
      options={mergedOptions}
      getOptionLabel={
        getOptionLabel
          ? (option) => getOptionLabel(option) as string
          : (option) => String(option.value)
      }
      slots={{
        listbox: infinite ? (VirtualListbox as any) : undefined,
      }}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      loading={isLoading}
      onInputChange={(_event, newInputValue) => {
        onInputChange(newInputValue);
      }}
      disableCloseOnSelect={multiple}
      slotProps={{
        listbox: infinite
          ? ({
              isFetchingNextPage,
              fetchNextPage,
              hasNextPage,
            } as any)
          : undefined,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={
            multiple && isFinite(maxSelected) ? `max ${maxSelected}` : undefined
          }
          slotProps={{
            input: {
              ...params.InputProps,
              sx: { width, minWidth, maxWidth },
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      popupIcon={
        <ExpandMoreIcon
          sx={{
            color: "text.primary",
            fontSize: 18,
          }}
        />
      }
      renderOption={
        renderOption
          ? renderOption
          : (props, option) => {
              const disabled = limitReached;
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.key}
                  sx={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: disabled
                        ? "transparent"
                        : "action.hover",
                    },
                  }}
                >
                  {String(option.value)}
                </Box>
              );
            }
      }
    />
  );
}
